import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useLang } from '../lib/lang';

// DML Brand tokens
const DML = {
  gold: '#C9975B',
  goldLight: '#E3CEB1',
  goldDark: '#9A6630',
  cream: '#FAF8F2',
  creamDark: '#F0EBE0',
  navy: '#161616',
  white: '#FFFFFF',
  fontHeading: "'Barlow', 'barlow-medium', sans-serif",
  fontBody: "'Barlow', 'avenir-lt-w01_35-light1475496', sans-serif",
};

export default function Login() {
  const { t, lang, setLang } = useLang();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isAr = lang === 'ar';
  const ff = isAr ? "'Noto Kufi Arabic', 'IBM Plex Sans Arabic', serif" : DML.fontBody;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(t.loginError);
    setLoading(false);
  };

  return (
    <div dir={t.dir} style={{
      minHeight: '100vh',
      background: DML.cream,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: ff,
      padding: '1rem',
      position: 'relative',
    }}>

      {/* Subtle gold border top */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, transparent, ${DML.gold}, transparent)` }} />

      {/* Lang toggle */}
      <div style={{ position: 'absolute', top: '1.5rem', [isAr ? 'left' : 'right']: '1.5rem' }}>
        <button onClick={() => setLang(isAr ? 'en' : 'ar')} style={{
          background: 'transparent', border: `1px solid ${DML.goldLight}`, borderRadius: '6px',
          padding: '5px 14px', fontSize: '12px', cursor: 'pointer', color: DML.goldDark,
          fontFamily: ff, letterSpacing: '0.5px'
        }}>
          {isAr ? 'English' : 'العربية'}
        </button>
      </div>

      {/* Logo block */}
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <img src="/dml-logo.png" alt="Dar Al Masri Law" style={{ height: '64px', objectFit: 'contain' }} />
        </div>

        <div style={{ width: '48px', height: '1px', background: DML.goldLight, margin: '0 auto 1rem' }} />
        <p style={{ fontSize: '13px', color: '#999', letterSpacing: '0.5px', fontFamily: ff }}>{t.loginSub}</p>
      </div>

      {/* Login card */}
      <div style={{
        background: DML.white,
        borderRadius: '2px',
        border: `1px solid ${DML.goldLight}`,
        padding: '2.25rem 2rem',
        width: '100%',
        maxWidth: '380px',
        boxShadow: '0 4px 32px rgba(201,151,91,0.08)',
      }}>
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px',
            padding: '10px 14px', marginBottom: '1.25rem', fontSize: '13px', color: '#b91c1c',
            fontFamily: ff
          }}>{error}</div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ fontSize: '11px', fontWeight: '500', color: '#888', display: 'block', marginBottom: '8px', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: ff }}>
              {t.emailLabel}
            </label>
            <input
              type="email" required value={email}
              onChange={e => setEmail(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', fontSize: '14px',
                border: `1px solid ${DML.goldLight}`, borderRadius: '2px', outline: 'none',
                fontFamily: ff, boxSizing: 'border-box', background: DML.cream, color: DML.navy,
              }}
              onFocus={e => e.target.style.borderColor = DML.gold}
              onBlur={e => e.target.style.borderColor = DML.goldLight}
            />
          </div>

          <div style={{ marginBottom: '1.75rem' }}>
            <label style={{ fontSize: '11px', fontWeight: '500', color: '#888', display: 'block', marginBottom: '8px', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: ff }}>
              {t.passwordLabel}
            </label>
            <input
              type="password" required value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', fontSize: '14px',
                border: `1px solid ${DML.goldLight}`, borderRadius: '2px', outline: 'none',
                fontFamily: ff, boxSizing: 'border-box', background: DML.cream, color: DML.navy,
              }}
              onFocus={e => e.target.style.borderColor = DML.gold}
              onBlur={e => e.target.style.borderColor = DML.goldLight}
            />
          </div>

          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '12px', background: DML.navy, color: DML.gold,
            border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: '500',
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
            fontFamily: ff, letterSpacing: '1.5px', textTransform: 'uppercase',
            transition: 'background 0.2s',
          }}
            onMouseEnter={e => { if (!loading) e.target.style.background = DML.goldDark; }}
            onMouseLeave={e => { if (!loading) e.target.style.background = DML.navy; }}
          >
            {loading ? t.signingIn : t.signIn}
          </button>
        </form>
      </div>

      <p style={{ marginTop: '2rem', fontSize: '11px', color: DML.goldLight, letterSpacing: '0.5px', fontFamily: ff }}>
        {isAr ? 'آمن ومشفّر · للموكلين المسجّلين فقط' : 'Secure & Encrypted · Registered Clients Only'}
      </p>

      {/* Gold border bottom */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, transparent, ${DML.gold}, transparent)` }} />
    </div>
  );
}
